/**
 * E-Mail-Import ("Kontowecker") data hooks (Phase 7, §API).
 *
 * `useEmailIngestAvailability` gates every entry point in the UI (`enabled`
 * mirrors `settings.email_ingest_enabled` being configured server-side) and is
 * cached like the FinTS availability query (1h `staleTime`, `useFints.ts`).
 *
 * The mailbox config is a 1:1 sidecar per account, so every query/mutation
 * below is addressed by `accountId`, never its own id (mirrors the API's own
 * `/accounts/{id}/email-config` addressing). `useEmailConfig` 404s
 * (`ApiRequestError.code === 'not_found'`) when the account has no config yet
 * — callers branch on that to show a create vs. edit form, it is not treated
 * as a query failure needing an `ErrorState`.
 *
 * Every mutation invalidates `qk.emailConfig(accountId)`; delete additionally
 * invalidates the `['email-ingest','messages',accountId]` prefix (the config's
 * messages are gone too, FK CASCADE). `fetch-now` can create/enrich
 * transactions (§Merge semantics), so it also invalidates the same
 * transaction-touching set as every other sync-like mutation in this app
 * (`useFints.ts`'s `invalidateSyncDownstream` precedent) — accounts included,
 * since a fetch can move `current_balance`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type {
  EmailIngestAvailabilityOut,
  EmailIngestConfigIn,
  EmailIngestConfigOut,
  EmailIngestFetchOut,
  EmailIngestTestOut,
  EmailMessageListMeta,
  EmailMessageOut,
  EmailMessageRawOut,
} from '../types.ts';

const STALE = 5 * 60 * 1000;

// ── Queries ────────────────────────────────────────────────────────────────

/** GET /api/email-ingest/availability — gates every E-Mail-Import entry point. */
export function useEmailIngestAvailability(): UseQueryResult<EmailIngestAvailabilityOut> {
  return useQuery({
    queryKey: qk.emailIngestAvailability,
    queryFn: () => api.get<EmailIngestAvailabilityOut>('/api/email-ingest/availability'),
    staleTime: 3600_000,
  });
}

export interface UseEmailConfigOptions {
  /** Caller-side gate (e.g. only fetch while the dialog is open and the
   * channel is available). Default `true`. */
  enabled?: boolean;
}

/** GET /api/accounts/{id}/email-config — 404s (`not_found`) when the account
 * has no mailbox configured yet; that is a normal "create mode" signal, not an
 * error state. */
export function useEmailConfig(
  accountId: number,
  options: UseEmailConfigOptions = {},
): UseQueryResult<EmailIngestConfigOut> {
  return useQuery({
    queryKey: qk.emailConfig(accountId),
    queryFn: () => api.get<EmailIngestConfigOut>(`/api/accounts/${accountId}/email-config`),
    staleTime: STALE,
    retry: false,
    enabled: options.enabled ?? true,
  });
}

export interface UseEmailMessagesOptions {
  enabled?: boolean;
}

export interface EmailMessagesPage {
  items: EmailMessageOut[];
  meta: EmailMessageListMeta | undefined;
}

/** GET /api/accounts/{id}/email-config/messages?limit=&offset= — paginated,
 * newest first (§API), the parser-development viewer's message list. */
export function useEmailMessages(
  accountId: number,
  params: { limit: number; offset: number },
  options: UseEmailMessagesOptions = {},
): UseQueryResult<EmailMessagesPage> {
  return useQuery({
    queryKey: qk.emailMessages(accountId, params),
    queryFn: async () => {
      const envelope = await api.getEnvelope<EmailMessageOut[], EmailMessageListMeta>(
        `/api/accounts/${accountId}/email-config/messages?limit=${params.limit}&offset=${params.offset}`,
      );
      return { items: envelope.data, meta: envelope.meta } satisfies EmailMessagesPage;
    },
    enabled: options.enabled ?? true,
  });
}

/** GET /api/accounts/{id}/email-config/messages/{message_id}/raw — loaded on
 * demand only (skips while `messageId` is `null`, `useReceiptCandidates`'s
 * on-demand pattern). */
export function useEmailMessageRaw(
  accountId: number,
  messageId: number | null,
): UseQueryResult<EmailMessageRawOut> {
  return useQuery({
    queryKey:
      messageId === null
        ? (['email-ingest', 'messages', 'raw', 'disabled'] as const)
        : qk.emailMessageRaw(accountId, messageId),
    queryFn: () =>
      api.get<EmailMessageRawOut>(
        `/api/accounts/${accountId}/email-config/messages/${messageId}/raw`,
      ),
    enabled: messageId !== null,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

export function useUpsertEmailConfig(
  accountId: number,
): UseMutationResult<EmailIngestConfigOut, Error, EmailIngestConfigIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EmailIngestConfigIn) =>
      api.put<EmailIngestConfigOut>(`/api/accounts/${accountId}/email-config`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.emailConfig(accountId) });
    },
  });
}

export function useDeleteEmailConfig(accountId: number): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del(`/api/accounts/${accountId}/email-config`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.emailConfig(accountId) });
      void qc.invalidateQueries({ queryKey: ['email-ingest', 'messages', accountId] });
    },
  });
}

/** POST .../email-config/test — connect/login/select only, nothing persisted;
 * no invalidation. */
export function useTestEmailConfig(
  accountId: number,
): UseMutationResult<EmailIngestTestOut, Error, void> {
  return useMutation({
    mutationFn: () => api.post<EmailIngestTestOut>(`/api/accounts/${accountId}/email-config/test`),
  });
}

/** POST .../email-config/fetch-now — one-shot `fetch_once`; may create/enrich
 * transactions and move `current_balance` (§Merge semantics), so it
 * invalidates the same downstream set as a sync run. */
export function useFetchNowEmailConfig(
  accountId: number,
): UseMutationResult<EmailIngestFetchOut, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<EmailIngestFetchOut>(`/api/accounts/${accountId}/email-config/fetch-now`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.emailConfig(accountId) });
      void qc.invalidateQueries({ queryKey: ['email-ingest', 'messages', accountId] });
      void qc.invalidateQueries({ queryKey: qk.accounts });
      void qc.invalidateQueries({ queryKey: qk.transactions });
      void qc.invalidateQueries({ queryKey: qk.inbox });
      void qc.invalidateQueries({ queryKey: qk.reports });
      void qc.invalidateQueries({ queryKey: qk.budgets });
    },
  });
}
